import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useRef } from 'react';
import { useBooleanState } from '../../../hooks/index/use-boolean-state';

type BaseItemType = {
  id: number | string;
  name: string;
  icon?: React.ReactElement;
};

export type DropdownPropsType<ItemType extends BaseItemType> = {
  selectedItems: ItemType[];
  setSelectedItems: React.Dispatch<React.SetStateAction<ItemType[]>>;
  items: ItemType[];
  allItem: ItemType;
  label: string;
  icon: React.ReactElement;
  tickSelectedItem?: boolean;
};

function formatSelectionSummary<ItemType extends BaseItemType>(items: ItemType[]): string {
  if (!items.length) return '';
  if (items.length === 1) return items[0].name;
  if (items.length === 2) return `${items[0].name} و ${items[1].name}`;
  if (items.length === 3) return `${items[0].name}، ${items[1].name} و ${items[2].name}`;
  return `${items[0].name}، ${items[1].name}، ${items[2].name} و ...`;
}

function Dropdown<ItemType extends BaseItemType>({
  selectedItems,
  setSelectedItems,
  items,
  allItem,
  label,
  icon,
  tickSelectedItem = false
}: DropdownPropsType<ItemType>) {
  const { value: isOpen, toggle: toggleIsOpen, setFalse: setClose } = useBooleanState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const setCloseRef = useRef(setClose);
  setCloseRef.current = setClose;

  const isAllSelected =
    selectedItems.length === 0 || selectedItems.some(item => item.id === allItem.id);
  const areAllItemsSelected = selectedItems.length === items.length && items.length > 0;
  const hasSelection = selectedItems.length > 0 && !isAllSelected;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (item: ItemType) => {
    setSelectedItems(prev => {
      const withoutAllItem = prev.filter(prevItem => prevItem.id !== allItem.id);
      const isAlreadySelected = withoutAllItem.some(prevItem => prevItem.id === item.id);

      if (isAlreadySelected) {
        const remaining = withoutAllItem.filter(prevItem => prevItem.id !== item.id);
        return remaining.length ? remaining : [allItem];
      }

      return [...withoutAllItem, item];
    });
  };

  const handleSelectAll = () => setSelectedItems([allItem]);

  const summaryText =
    isAllSelected || areAllItemsSelected ? allItem.name : formatSelectionSummary(selectedItems);

  return (
    <div ref={dropdownRef} className='relative w-full sm:w-[48%]'>
      <button
        type='button'
        onClick={toggleIsOpen}
        className={`
          flex w-full items-center justify-between
          rounded-xl border bg-white px-4
          py-2 shadow-sm transition-all duration-200
          ${
            hasSelection
              ? 'border-amber-400'
              : isOpen
              ? 'border-amber-300 ring-2 ring-amber-50'
              : 'border-zinc-200 hover:border-amber-200'
          }
        `}
      >
        <div className='flex items-center gap-3'>
          <span className='text-amber-500'>{icon}</span>

          <div className='flex flex-col items-start'>
            <span className='text-[10px] font-medium text-zinc-500'>{label}:</span>
            <span
              className={`text-sm font-semibold ${
                hasSelection ? 'text-amber-700' : 'text-zinc-800'
              }`}
            >
              {summaryText}
            </span>
          </div>
        </div>

        <ChevronDownIcon
          width={16}
          className={`
            transition-all duration-200
            ${isOpen ? 'rotate-180 text-amber-600' : 'text-zinc-400'}
          `}
        />
      </button>

      {isOpen && (
        <div
          className='custom-scrollbar absolute top-full left-0 right-0 z-50 mt-2
          max-h-[220px] overflow-auto rounded-2xl
          border border-zinc-200
          bg-white shadow-lg'
        >
          <button
            type='button'
            onClick={handleSelectAll}
            className={`
              flex w-full items-center justify-between
              rounded-lg px-4 py-3 text-sm transition-all duration-150
              ${
                isAllSelected
                  ? 'bg-amber-100 font-extrabold text-amber-800 shadow-sm'
                  : 'text-zinc-700 hover:bg-zinc-50'
              }
            `}
          >
            <span>{allItem.name}</span>
            {tickSelectedItem && isAllSelected && (
              <CheckIcon width={20} className='text-amber-700' />
            )}
          </button>

          {items.map(item => {
            const isSelected = selectedItems.some(selectedItem => selectedItem.id === item.id);

            return (
              <button
                type='button'
                key={String(item.id)}
                onClick={() => handleSelect(item)}
                className={`
                  flex w-full items-center justify-between
                  rounded-lg px-4 py-3 text-sm transition-all duration-150
                  ${
                    isSelected
                      ? 'bg-amber-100 font-extrabold text-amber-800 shadow-sm'
                      : 'text-zinc-700 hover:bg-zinc-50'
                  }
                `}
              >
                <span>{item.name}</span>

                <div className='flex items-center gap-2'>
                  {item.icon ? (
                    <div className='text-amber-500'>{item.icon}</div>
                  ) : (
                    tickSelectedItem &&
                    isSelected && <CheckIcon width={20} className='text-amber-700' />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
